import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HCHeatmap from "highcharts/modules/heatmap";
import HCExporting from "highcharts/modules/exporting";
import useCustomLogic from "../../../Hooks/UseCustomLogic";
import { html, text } from "d3";

HCHeatmap(Highcharts);
HCExporting(Highcharts);

const HeatmapChart = ({ heatMapGraphData, dropdownState }) => {
  function interpolateColor(color1, color2, factor) {
    const c1 = hexToRgb(color1);
    const c2 = hexToRgb(color2);

    const result = {
      r: Math.round(c1.r + factor * (c2.r - c1.r)),
      g: Math.round(c1.g + factor * (c2.g - c1.g)),
      b: Math.round(c1.b + factor * (c2.b - c1.b)),
    };

    return `rgb(${result.r}, ${result.g}, ${result.b})`;
  }

  function hexToRgb(hex) {
    hex = hex.replace("#", "");
    const bigint = parseInt(hex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  function getHeatmapBarColor(percent) {
    const stops = [
      { stop: 0, color: "#E15759" },
      { stop: 25, color: "#F4A261" },
      { stop: 50, color: "#FFD966" },
      { stop: 75, color: "#7AC36A" },
      { stop: 100, color: "#7AC36A" },
    ];

    for (let i = 0; i < stops.length - 1; i++) {
      const current = stops[i];
      const next = stops[i + 1];

      if (percent >= current.stop && percent <= next.stop) {
        const range = next.stop - current.stop;
        const factor = (percent - current.stop) / range;
        return interpolateColor(current.color, next.color, factor);
      }
    }

    return "#E15759";
  }

  const { isSm, isXs, isXss } = useCustomLogic();

  const TILE_WIDTH = 100; // Width of one tile (column)
  const TILE_HEIGHT = 35; // Height of one tile (row)

  const categories = [
    "Getting There",
    "Navigation",
    "Waiting Area",
    "Barrier-Free Access",
    "Hygiene",
    "Case Display",
    "Security",
    "Amenities",
    "Website",
    // "Total",
  ];

  const categoryMapping = {
    "Getting There": "getting_there_number",
    Navigation: "navigation_number",
    "Waiting Area": "waiting_number",
    "Barrier-Free Access": "disability_number",
    Hygiene: "hygiene_number",
    "Case Display": "case_info_number",
    Security: "security_number",
    Amenities: "amenities_number",
    Website: "website_number",
    // Total: "total",
  };
  const filteredStates = heatMapGraphData?.filter(
    (data) => data?.state !== "All" && (dropdownState === "All" || data?.district_name !== "All")
  );

  const stateOrDistrictList =
    filteredStates?.map((item) => {
      let names = dropdownState == "All" ? item.state : item.district_name;
      return names;
    }) || [];

  const heatmapData = [];
  filteredStates?.forEach((item, y) => {
    categories.forEach((category, x) => {
      const value = item[categoryMapping[category]] ?? 0;
      heatmapData.push([x, y, value]);
    });
  });

  const chartWidth = TILE_WIDTH * categories.length;
  const chartHeight = TILE_HEIGHT * stateOrDistrictList.length + 120;

  const heatmapOptions = {
    chart: {
      type: "heatmap",
      marginTop: 120,
      marginBottom: 0,
      plotBorderWidth: 0,
      width: chartWidth,
      height: chartHeight,
    },
    title: { text: "" },
    exporting: { enabled: false },
    xAxis: {
      categories: categories,
      title: { text: "" },
      opposite: true,
      labels: {
        rotation: 0,
        enabled: true,
        style: {
          fontSize: isXss ? "10px" : isXs ? "10px" : isSm ? "11px" : "12px",
          whiteSpace: "normal",
          textOverflow: "none",
          width: TILE_WIDTH - 10 + "px",
        },
        useHTML: true,
      },
    },
    yAxis: {
      categories: stateOrDistrictList,
      title: { text: "" },
      reversed: true,
      labels: {
        useHTML: true,
        style: {
          fontSize: isXss ? "10px" : isXs ? "10px" : isSm ? "11px" : "12px",
          width: "110px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        formatter: function () {
          const label = this.value;
          return `<div style="
        max-width: 110px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      ">${label}</div>`;
        },
      },
    },
    colorAxis: {
      stops: [
        [0, "#E15759"],
        [0.2, "#F4A261"],
        [0.5, "#FFD966"],
        [0.75, "#7AC36A"],
        [1, "#7AC36A"],
      ],
      min: 0,
      max: 100,
    },
    legend: {
      align: "right",
      layout: "horizontal",
      margin: 0,
      verticalAlign: "top",
      y: 0,
      x: 0,
      zIndex: 10,
      symbolHeight: 280,
    },
    tooltip: {
      enabled: true,
      formatter: function () {
        return `<b>${stateOrDistrictList[this.point.y]}</b> - <b>${
          categories[this.point.x]
        }</b>: <b>${this.point.value}%</b>`;
      },
    },
    series: [
      {
        name: "Survey Data",
        borderWidth: 3,
        borderColor: "#fff",
        data: heatmapData,
        colsize: 1,
        rowsize: 1,
        dataLabels: {
          enabled: false,
        },
      },
    ],
  };

  return (
    // <div
    //   style={{
    //     width: "100%",
    //     overflowX: "auto",
    //     overflowY: "hidden",
    //     margin: "0 auto",
    //   }}
    // >
    //   <div
    //     style={{
    //       width: `${chartWidth}px`,
    //       height: `${chartHeight}px`,
    //     }}
    //   >
    //     <HighchartsReact highcharts={Highcharts} options={heatmapOptions} />
    //   </div>
    // </div>

    <div
      style={{
        display: "flex",
        overflowX: "auto",
        width: "100%",
      }}
    >
      {/* HEATMAP */}
      <div style={{ width: `${chartWidth}px`, height: `${chartHeight}px` }}>
        <HighchartsReact highcharts={Highcharts} options={heatmapOptions} />
      </div>

      {/* PERCENTAGE COLUMN WITH BARS */}
      <div
        style={{
          paddingTop: "94px",
          height: `${TILE_HEIGHT * stateOrDistrictList.length} + 120px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "1px",
        }}
      >
        {/* Header label for clarity (optional) */}
        <div
          style={{
            height: `25px`,
            textAlign: "right",
            color: "rgb(51, 51, 51)",
            fontSize: isXss ? "10px" : isXs ? "10px" : isSm ? "11px" : "12px",
          }}
        >
          Total
        </div>
        <div style={{ display: "grid", gap: "0px" }}>
          {filteredStates?.map((item, idx) => {
            const percent = item.total ?? 0;
            const bgColor = getHeatmapBarColor(percent);

            return (
              <div
                key={idx}
                style={{
                  height: `${TILE_HEIGHT}px`,
                  display: "flex",
                  alignItems: "center",
                  gap: "3px",
                  fontSize: isXss ? "10px" : isXs ? "10px" : isSm ? "11px" : "12px",
                  whiteSpace: "nowrap",
                }}
              >
                <div
                  style={{
                    height: "30px",
                    width: "60px",
                    background: "#fff",
                    borderRadius: "1px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${percent}%`,
                      height: "100%",
                      backgroundColor: bgColor,
                    }}
                  ></div>
                </div>
                <span>{percent}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HeatmapChart;
